import React, { useState, useEffect } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useGetBasicInfoQuery } from '../redux/apiSlice'; // Adjust path as needed

const Sidebar = ({ sidebarOpen, setSidebarOpen, onLogout }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [mounted, setMounted] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  // Basic Info API call
  const { 
    data: basicInfoData, 
    isLoading: basicInfoLoading, 
    error: basicInfoError 
  } = useGetBasicInfoQuery();

  useEffect(() => {
    setMounted(true);
  }, []);

  // Extract data from API response
  const basicInfo = basicInfoData?.data || {};
  
  // Get full name from basic info (fname + lname)
  const fullName = basicInfo.fname && basicInfo.lname 
    ? `${basicInfo.fname} ${basicInfo.lname}`
    : 'Dr. Sarah Johnson';

  // Get first name for display
  const firstName = basicInfo.fname || 'Dr. Sarah';

  // Get profile image from API or use default
  const profileImage = basicInfo.image || "https://ui-avatars.com/api/?name=User&background=3B82F6&color=ffffff&size=256&bold=true&font-size=0.8";

  // Get user role (default to "Instructor")
  const userRole = 'Instructor'; // You can update this if your API provides role info

  const navigationItems = [
    {
      name: 'Dashboard',
      href: '/dashboard',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
        </svg>
      ),
      exact: true
    },
    {
      name: 'Profile Settings',
      href: '/profile',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
      ),
      exact: true
    },
    {
      name: 'Earnings',
      href: '/earnings',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v1m0 6v1m0-1v1m0-1h-1m1 0h1" />
        </svg>
      ),
      exact: true
    },
    {
      name: 'Course List',
      href: '/courses',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
        </svg>
      ),
      exact: false // This allows nested routes like /courses/create to be active
    },
  ];

  // Enhanced isActive function for nested routes
  const isActive = (href, exact = false) => {
    if (exact) {
      return location.pathname === href;
    }
    // For non-exact matches, check if current path starts with href
    // This makes /courses/create active when href is /courses
    return location.pathname === href || location.pathname.startsWith(href + '/');
  };

  // Handle settings icon click - navigate to profile settings
  const handleSettingsClick = () => {
    navigate('/profile');
    setSidebarOpen(false);
  };

  const handleLogoutClick = () => {
    setShowLogoutConfirm(true);
  };

  const confirmLogout = () => {
    setShowLogoutConfirm(false);
    onLogout();
  };

  const cancelLogout = () => {
    setShowLogoutConfirm(false);
  };

  return (
    <>
      {/* Enhanced Mobile Sidebar Backdrop with Animation - Only when sidebarOpen changes */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 h-full z-40 md:hidden"
          onClick={() => setSidebarOpen(false)}
        >
          <div className="fixed inset-0 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 bg-opacity-75 backdrop-blur-sm transition-opacity duration-300" aria-hidden="true"></div>
        </div>
      )}

      {/* Enhanced Sidebar with Premium Styling - Fixed and hidden on 640px */}
      <div className={`
        fixed inset-y-0 left-0 z-50
        w-72 bg-gradient-to-b from-white via-green-50 to-white
        border-r border-green-200 shadow-2xl
        transform transition-transform duration-300 ease-out
        md:translate-x-0 md:flex md:flex-col
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        
        {/* Enhanced Logo Section with Gradient */}
        <div className="flex items-center justify-between h-20 flex-shrink-0 px-6 border-b border-green-200 bg-gradient-to-r from-green-500 to-green-600">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-white bg-opacity-20 rounded-2xl flex items-center justify-center shadow-lg backdrop-blur-sm transform hover:scale-110 transition-transform duration-300">
              <span className="text-white font-bold text-lg drop-shadow-lg">LMS</span>
            </div>
            <div>
              <h1 className="text-xl font-bold text-white drop-shadow-lg">Instructor Panel</h1>
              <div className="w-16 h-1 bg-white bg-opacity-50 rounded-full mt-1 animate-pulse"></div>
            </div>
          </div>
          
          {/* Enhanced Close button - Only show on mobile */}
          <button
            className="md:hidden p-3 rounded-2xl text-white hover:text-green-100 hover:bg-white hover:bg-opacity-20 transition-all duration-300 transform hover:scale-110 hover:rotate-90 backdrop-blur-sm"
            onClick={() => setSidebarOpen(false)}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Enhanced Navigation with Hover Animations Only */}
        <nav className="flex-1 flex flex-col overflow-y-auto py-8 scrollbar-hide">
          <div className="flex-1 px-6 space-y-3">
            {navigationItems.map((item, index) => {
              const active = isActive(item.href, item.exact);
              
              return (
                <NavLink
                  key={item.name}
                  to={item.href}
                  className={`
                    group relative flex items-center px-4 py-4 text-base font-semibold rounded-2xl
                    transition-all duration-300 ease-out
                    ${active 
                      ? 'text-white scale-105 shadow-2xl' 
                      : 'text-gray-700 hover:text-gray-900 hover:scale-105 hover:shadow-xl'
                    }
                  `}
                  onClick={() => setSidebarOpen(false)}
                >
                  {/* Animated Background with Gradient */}
                  <div className={`
                    absolute inset-0 rounded-2xl transition-all duration-300 ease-out
                    ${active 
                      ? 'bg-gradient-to-r from-green-500 to-green-600 shadow-lg' 
                      : 'bg-transparent group-hover:bg-green-50 group-hover:bg-opacity-50'
                    }
                  `}></div>
                  
                  {/* Animated Border Effect */}
                  <div className={`
                    absolute inset-0 rounded-2xl border-2 transition-all duration-300 ease-out
                    ${active 
                      ? 'border-green-400 border-opacity-50' 
                      : 'border-transparent group-hover:border-green-200'
                    }
                  `}></div>

                  {/* Icon with Enhanced Animation */}
                  <div className={`
                    relative z-10 mr-4 flex-shrink-0 transition-all duration-300 ease-out
                    ${active 
                      ? 'text-white scale-110' 
                      : 'text-gray-500 group-hover:text-green-600 group-hover:scale-110'
                    }
                    transform group-hover:rotate-12
                  `}>
                    {item.icon}
                  </div>

                  {/* Text with Enhanced Styling */}
                  <span className="relative z-10 font-semibold tracking-wide">
                    {item.name}
                  </span>
                  
                  {/* Enhanced Active Indicator */}
                  {active && (
                    <div className="relative z-10 ml-auto">
                      <div className="flex space-x-1">
                        <div className="w-2 h-2 bg-white rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                        <div className="w-2 h-2 bg-white rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                        <div className="w-2 h-2 bg-white rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                      </div>
                    </div>
                  )}

                  {/* Hover Arrow Indicator */}
                  {!active && (
                    <div className="relative z-10 ml-auto opacity-0 group-hover:opacity-100 transform group-hover:translate-x-1 transition-all duration-300 ease-out">
                      <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  )}

                  {/* Ripple Effect on Click */}
                  <div className="absolute inset-0 rounded-2xl overflow-hidden">
                    <div className="absolute inset-0 bg-white bg-opacity-20 opacity-0 group-active:opacity-100 transition-opacity duration-200 ease-out"></div>
                  </div>
                </NavLink>
              );
            })}
          </div>

          {/* Enhanced User Profile Section */}
          <div className="flex-shrink-0 border-t border-green-200 mx-6 mt-8 pt-8 pb-4">
            <div className="flex items-center space-x-4 group cursor-pointer p-4 rounded-2xl hover:bg-green-50 transition-all duration-300 ease-out transform hover:scale-105 mb-4">
              <div className="relative flex-shrink-0">
                {basicInfoLoading ? (
                  // Loading skeleton for profile image
                  <div className="h-14 w-14 rounded-2xl border-4 border-gray-200 bg-gray-100 animate-pulse"></div>
                ) : (
                  <>
                    <img
                      className="h-14 w-14 rounded-2xl border-4 border-green-300 shadow-lg group-hover:border-green-400 group-hover:shadow-xl transition-all duration-300 ease-out"
                      src={profileImage}
                      alt={fullName}
                      onError={(e) => {
                        e.target.src = "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80";
                      }}
                    />
                    {/* Online Status with Pulse Animation */}
                    <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 border-2 border-white rounded-full animate-pulse shadow-lg"></div>
                    {/* Hover Ring */}
                    <div className="absolute inset-0 rounded-2xl border-2 border-transparent group-hover:border-green-300 transition-all duration-300 ease-out"></div>
                  </>
                )}
              </div>
              
              <div className="min-w-0 flex-1">
                <p className="text-sm font-bold text-gray-900 truncate group-hover:text-green-700 transition-colors duration-300 ease-out transform group-hover:translate-x-1">
                  {basicInfoLoading ? 'Loading...' : basicInfoError ? 'Dr. Sarah Johnson' : fullName}
                </p>
                <p className="text-xs font-semibold text-green-600 truncate group-hover:text-green-500 transition-colors duration-300 ease-out transform group-hover:translate-x-1">
                  {basicInfoLoading ? 'Loading...' : basicInfoError ? 'Senior Instructor' : userRole}
                </p>
                <div className="w-12 h-1 bg-gradient-to-r from-green-400 to-green-600 rounded-full mt-2 group-hover:w-16 transition-all duration-300 ease-out"></div>
              </div>

              {/* Settings Icon with Enhanced Animation - Now navigates to Profile Settings */}
              <button 
                onClick={handleSettingsClick}
                className="p-3 text-gray-400 hover:text-green-600 hover:bg-green-100 rounded-2xl transition-all duration-300 ease-out transform hover:scale-110 hover:rotate-45"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </button>
            </div>

            {/* Logout Button */}
            <button
              onClick={handleLogoutClick}
              className="w-full flex items-center justify-center space-x-3 px-4 py-3 text-base font-semibold text-gray-700 bg-red-50 hover:bg-red-100 hover:text-red-700 border border-red-200 rounded-2xl transition-all duration-300 ease-out transform hover:scale-105 hover:shadow-lg group"
            >
              <svg className="w-5 h-5 text-red-500 group-hover:text-red-600 transition-colors duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              <span className="font-semibold">Logout</span>
              <div className="w-2 h-2 bg-red-400 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300 animate-pulse"></div>
            </button>
          </div>
        </nav>
      </div>

      {/* Logout Confirmation Popup */}
      {showLogoutConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 animate-fade-in">
          <div className="bg-white rounded-2xl p-6 sm:p-8 mx-4 max-w-sm w-full shadow-2xl transform animate-scale-in">
            {/* Warning Icon */}
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.35 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
            </div>

            {/* Popup Content */}
            <div className="text-center">
              <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">
                Are you sure?
              </h3>
              <p className="text-sm sm:text-base text-gray-600 mb-6">
                You will be logged out of your account. Any unsaved changes will be lost.
              </p>

              {/* Action Buttons */}
              <div className="flex space-x-3 sm:space-x-4">
                <button
                  onClick={cancelLogout}
                  className="flex-1 px-4 py-2 sm:py-3 text-sm sm:text-base font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-all duration-300 transform hover:scale-105"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmLogout}
                  className="flex-1 px-4 py-2 sm:py-3 text-sm sm:text-base font-medium text-white bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg"
                >
                  Logout
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Custom CSS for animations */}
      <style jsx>{`
        @keyframes fade-in {
          from { 
            opacity: 0; 
          }
          to { 
            opacity: 1; 
          }
        }
        
        @keyframes scale-in {
          from { 
            opacity: 0; 
            transform: scale(0.9); 
          }
          to { 
            opacity: 1; 
            transform: scale(1); 
          }
        }
        
        .animate-fade-in {
          animation: fade-in 0.3s ease-out both;
        }
        
        .animate-scale-in {
          animation: scale-in 0.3s ease-out both;
        }
      `}</style>
    </>
  );
};

export default Sidebar;